import { butterworthBandpass, filtfilt } from './biquad.ts';
import { hilbertEnvelope } from './analytic.ts';
import { speedOfSound } from './acoustics.ts';
import { movingAverage, refinePeak, toDb } from './util.ts';

export interface Echo {
  /** Distance to the reflecting surface, in metres. */
  distanceM: number;
  /** Round-trip delay after the direct sound, in milliseconds. */
  delayMs: number;
  /** Echo strength relative to the direct sound, in dB (negative). */
  levelDb: number;
  /** Local reverberant background at this range, in dB relative to direct. */
  backgroundDb: number;
  /**
   * How far this arrival stands above its own local background. The honest
   * confidence measure: a wall is not "loud", it is louder than the
   * reverberation arriving at the same moment.
   */
  excessDb: number;
  index: number;
}

export interface RangeProfile {
  /** Distance axis in metres, one entry per sample of the analysis window. */
  distancesM: Float64Array;
  /** Echo strength in dB relative to the direct sound. */
  levelsDb: Float64Array;
  echoes: Echo[];
  /** Below this distance the direct blast masks everything. The bat problem. */
  blindRangeM: number;
  maxRangeM: number;
  speedOfSoundMs: number;
}

export interface RangeProfileOptions {
  /** Sub-sample index of the direct arrival, from `findPeak`. */
  directIndex: number;
  temperatureC?: number;
  /**
   * Band-pass applied before enveloping. Two effects pull in the same
   * direction here. A deconvolved impulse rings for about one period of the
   * low edge, so 200 Hz would smear the first 5 ms - nearly a metre. And
   * detection improves with bandwidth: a specular arrival's peak scales with
   * bandwidth while the diffuse tail's level scales with its square root, so
   * every doubling of bandwidth buys 3 dB of echo-to-reverb. Wide and high.
   */
  bandLowHz?: number;
  bandHighHz?: number;
  /** Nearest reportable distance. Inside this, the direct sound dominates. */
  blindRangeM?: number;
  maxRangeM?: number;
  /**
   * Reject echoes weaker than this, relative to the direct sound. The default
   * is deep because real echoes are: spherical spreading alone puts a wall at
   * 1 m some 32 dB below a direct blast that only travelled 5 cm.
   */
  thresholdDb?: number;
  /**
   * CFAR detection threshold: how far above the locally estimated background
   * an arrival must stand. For a Rayleigh-distributed diffuse envelope the
   * false-alarm rate per cell is exp(-10^(dB/10)), so 10 dB gives roughly one
   * false echo per 20000 range cells and 6 dB gives one per 50.
   */
  detectionThresholdDb?: number;
  /**
   * Guard distance excluded either side of the cell under test when
   * estimating background, so a strong reflection does not raise the
   * threshold that judges it.
   */
  guardRangeM?: number;
  /** Half-width of the background estimation window, in metres. */
  trainingRangeM?: number;
  /** Minimum spacing between reported echoes. */
  minSeparationM?: number;
  maxEchoes?: number;
  /**
   * Envelope smoothing, in seconds. Matched to the arrival, not larger: a
   * specular reflection is about as wide as the inverse of the analysis
   * bandwidth, and averaging wider than that flattens the echo faster than it
   * flattens the noise around it. At 13 kHz of bandwidth the pulse is ~0.08 ms
   * wide, which is also the resolution limit - about 1.3 cm of range.
   */
  envelopeSmoothingSec?: number;
  /**
   * Speaker-to-microphone spacing. The direct blast travels this far, so it is
   * not a true zero-time reference; ignoring it biases every distance by half
   * the spacing. Roughly 5 cm for a phone's bottom speaker and bottom mic.
   */
  speakerMicSpacingM?: number;
}

/**
 * Echo strength against distance - the 1D sonar trace.
 *
 * One microphone gives range but no bearing, so this is a profile, not a map:
 * "something big is 2.8 m away", not "the wall is 2.8 m to your left".
 * Bearing needs the phone to rotate, which is what the polar mode adds.
 */
export function computeRangeProfile(
  ir: ArrayLike<number>,
  sampleRate: number,
  options: RangeProfileOptions,
): RangeProfile {
  const {
    directIndex,
    temperatureC = 20,
    bandLowHz = 1000,
    bandHighHz = 14000,
    blindRangeM = 0.35,
    maxRangeM = 10,
    thresholdDb = -60,
    detectionThresholdDb = 8,
    guardRangeM = 0.12,
    trainingRangeM = 1,
    minSeparationM = 0.2,
    maxEchoes = 8,
    envelopeSmoothingSec = 0.00008,
    speakerMicSpacingM = 0.05,
  } = options;

  const c = speedOfSound(temperatureC);
  // Analyse past the reported range and trim afterwards. Filter settling and
  // the CFAR training window both need data on the far side of the last cell
  // anyone looks at; without the margin, the last metre of every profile is
  // an artefact of its own edge.
  const marginM = Math.max(2 * trainingRangeM, 2);
  const analysisDelaySec = (2 * (maxRangeM + marginM)) / c;
  const start = Math.max(0, Math.floor(directIndex) - Math.round(0.002 * sampleRate));
  const end = Math.min(ir.length, Math.floor(directIndex) + Math.ceil(analysisDelaySec * sampleRate) + 1);

  const window = new Float64Array(Math.max(0, end - start));
  for (let i = 0; i < window.length; i++) window[i] = ir[start + i];

  const bandLimited = filtfilt(
    window,
    butterworthBandpass(bandLowHz, Math.min(bandHighHz, sampleRate * 0.45), sampleRate, 4),
  );
  const envelope = movingAverage(
    hilbertEnvelope(bandLimited),
    Math.max(1, Math.round(sampleRate * envelopeSmoothingSec)),
  );

  // Re-find the direct arrival inside the band-limited envelope: filtering
  // moves it by a fraction of a sample, and every distance is relative to it.
  const directLocal = clampIndex(Math.round(directIndex) - start, 0, envelope.length - 1);
  const searchLo = clampIndex(directLocal - Math.round(0.001 * sampleRate), 0, envelope.length - 1);
  const searchHi = clampIndex(directLocal + Math.round(0.001 * sampleRate), 0, envelope.length - 1);
  let peakIndex = searchLo;
  for (let i = searchLo; i <= searchHi; i++) if (envelope[i] > envelope[peakIndex]) peakIndex = i;
  const directFrac = refinePeak(envelope, peakIndex);
  const directLevel = envelope[peakIndex];

  const distancesM = new Float64Array(envelope.length);
  const levelsDb = new Float64Array(envelope.length);
  for (let i = 0; i < envelope.length; i++) {
    const delaySec = (i - directFrac) / sampleRate;
    distancesM[i] = (delaySec * c + speakerMicSpacingM) / 2;
    levelsDb[i] = toDb(envelope[i], directLevel);
  }

  const echoes = pickEchoes(envelope, distancesM, levelsDb, {
    blindRangeM,
    maxRangeM,
    thresholdDb,
    detectionThresholdDb,
    guardRangeM,
    trainingRangeM,
    minSeparationM,
    maxEchoes,
    sampleRate,
    speedMs: c,
    directFrac,
    spacingM: speakerMicSpacingM,
    directLevel,
  });

  // Trim the margin off the reported profile.
  let reportFrom = 0;
  let reportTo = distancesM.length;
  while (reportFrom < distancesM.length && distancesM[reportFrom] < -0.2) reportFrom++;
  while (reportTo > reportFrom && distancesM[reportTo - 1] > maxRangeM) reportTo--;

  return {
    distancesM: distancesM.slice(reportFrom, reportTo),
    levelsDb: levelsDb.slice(reportFrom, reportTo),
    echoes: echoes.map((e) => ({ ...e, index: e.index - reportFrom })),
    blindRangeM,
    maxRangeM,
    speedOfSoundMs: c,
  };
}

interface PickOptions {
  blindRangeM: number;
  maxRangeM: number;
  thresholdDb: number;
  detectionThresholdDb: number;
  guardRangeM: number;
  trainingRangeM: number;
  minSeparationM: number;
  maxEchoes: number;
  sampleRate: number;
  speedMs: number;
  directFrac: number;
  spacingM: number;
  directLevel: number;
}

/**
 * Cell-averaging CFAR detection, as radar and sonar do it.
 *
 * A fixed threshold cannot work here. The background an echo has to be seen
 * against is the room's own reverberation, which starts high and decays across
 * the whole profile - so a threshold that finds the far wall drowns in tail
 * near the front, and one that is clean near the front misses everything far
 * away. CFAR tests every range cell against a background estimated from its
 * own neighbours, with a guard band so a strong echo cannot raise the
 * threshold that judges it. The result is scale-free: "this arrival stands
 * 14 dB above the reverberation arriving at the same instant".
 */
function pickEchoes(
  envelope: Float64Array,
  distancesM: Float64Array,
  levelsDb: Float64Array,
  opts: PickOptions,
): Echo[] {
  const cellsPerMetre = (2 / opts.speedMs) * opts.sampleRate;
  const guard = Math.max(1, Math.round(opts.guardRangeM * cellsPerMetre));
  const training = Math.max(guard + 4, Math.round(opts.trainingRangeM * cellsPerMetre));
  const separationSamples = Math.max(2, Math.round(opts.minSeparationM * cellsPerMetre));
  const thresholdFactor = Math.pow(10, opts.detectionThresholdDb / 10);
  const directPower = Math.max(opts.directLevel * opts.directLevel, 1e-30);

  const power = new Float64Array(envelope.length);
  for (let i = 0; i < envelope.length; i++) power[i] = envelope[i] * envelope[i];
  const cumulative = new Float64Array(power.length + 1);
  for (let i = 0; i < power.length; i++) cumulative[i + 1] = cumulative[i] + power[i];

  const meanPower = (lo: number, hi: number): number => {
    const a = Math.max(0, lo);
    const b = Math.min(power.length, hi);
    return b > a ? (cumulative[b] - cumulative[a]) / (b - a) : 0;
  };

  const candidates: Echo[] = [];
  for (let i = 1; i < levelsDb.length - 1; i++) {
    if (distancesM[i] < opts.blindRangeM || distancesM[i] > opts.maxRangeM) continue;
    if (levelsDb[i] < opts.thresholdDb) continue;
    if (!(levelsDb[i] >= levelsDb[i - 1] && levelsDb[i] > levelsDb[i + 1])) continue;

    // Background from both flanks with the guard band excluded, taking the
    // quieter side so a strong reflection cannot mask a real one just behind it.
    const left = meanPower(i - guard - training, i - guard);
    const right = meanPower(i + guard, i + guard + training);
    const background = left > 0 && right > 0 ? Math.min(left, right) : Math.max(left, right);
    if (!(background > 0) || power[i] < background * thresholdFactor) continue;

    const refined = refinePeak(levelsDb, i);
    const delaySec = (refined - opts.directFrac) / opts.sampleRate;
    const backgroundDb = 10 * Math.log10(background / directPower);
    candidates.push({
      index: i,
      distanceM: (delaySec * opts.speedMs + opts.spacingM) / 2,
      delayMs: delaySec * 1000,
      levelDb: levelsDb[i],
      backgroundDb,
      excessDb: levelsDb[i] - backgroundDb,
    });
  }

  // Strongest-first greedy thinning, so one wall reports once.
  candidates.sort((a, b) => b.excessDb - a.excessDb);
  const accepted: Echo[] = [];
  for (const candidate of candidates) {
    if (accepted.length >= opts.maxEchoes) break;
    if (accepted.some((e) => Math.abs(e.index - candidate.index) < separationSamples)) continue;
    accepted.push(candidate);
  }

  return accepted.sort((a, b) => a.distanceM - b.distanceM);
}

function clampIndex(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
